import { useEffect, useState, type FormEvent } from 'react'
import type { Project } from '../api/types'
import { useBoardState, useStore } from '../store/context'
import { needsServer } from '../components/needsServer'
import { useMounted } from '../components/layout'
import { useUnsavedChanges } from '../components/unsaved'
import { useWorkspaceDraft } from '../workspace/drafts'
import { useSettingsProject } from './selection'
import { toast } from '../components/toasts'
import { settingsWords } from './words'
import { GitHubSettings } from './GitHubSettings'
import { RunnerRepositorySettings } from './RunnerRepositorySettings'

export function RepositorySettings() {
  const project = useSettingsProject()
  return project ? <><RepositoryForm key={project.id} project={project} /><GitHubSettings key={`${project.id}/${project.repository}`} project={project} /></> : null
}

/** The remote and branch in today's fields, set together through the new server's op. */
function RepositoryForm({ project }: { project: Project }) {
  const store = useStore()
  const { offline } = useBoardState()
  const mounted = useMounted()
  // Empty strings mean no local edit: the saved fields continue to follow live updates.
  const [edit, setEdit] = useWorkspaceDraft('repository.edit', '')
  const saved = JSON.stringify([project.repository?.split('/')[0] ?? '', project.repository?.split('/')[1] ?? '', project.branch])
  const read = (value: string): [string, string, string] | null => {
    try {
      const parts: unknown = JSON.parse(value)
      return Array.isArray(parts) && parts.length === 3 && parts.every(part => typeof part === 'string') ? parts as [string, string, string] : null
    } catch { return null }
  }
  const [owner, name, branch] = read(edit) ?? read(saved) ?? ['', '', 'main']
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  useUnsavedChanges(edit !== '' && edit !== saved)
  useEffect(() => { if (edit === saved) setEdit('') }, [edit, saved, setEdit])
  const change = (parts: [string, string, string]) => { setEdit(JSON.stringify(parts)); setProblem(null) }
  async function save(event: FormEvent) {
    event.preventDefault()
    if (store.getState().offline || busy || !owner.trim() || !name.trim()) return
    setBusy(true); setProblem(null)
    const outcome = await store.setProjectRepository(project.id, owner, name, branch)
    if (!mounted.current) return
    setBusy(false)
    if (outcome.ok) { setEdit(''); toast(settingsWords.saved) }
    else setProblem(outcome.message)
  }
  return <section className="settings-section">
    <RunnerRepositorySettings project={project.id} />
    <form className="setup-form" onSubmit={event => void save(event)}>
      <label className="field"><span>Owner</span><input value={owner} required disabled={busy} autoComplete="off" spellCheck={false} onChange={event => change([event.target.value, name, branch])} /></label>
      <label className="field"><span>Repository name</span><input value={name} required disabled={busy} autoComplete="off" spellCheck={false} onChange={event => change([owner, event.target.value, branch])} /></label>
      <label className="field"><span>Default branch</span><input value={branch} disabled={busy} autoComplete="off" spellCheck={false} onChange={event => change([owner, name, event.target.value])} /></label>
      {problem && <p role="alert" className="text-danger">{problem}</p>}
      <div className="form-actions"><button className="button" disabled={offline || busy || !owner.trim() || !name.trim()} {...needsServer(offline)}>{busy ? 'Saving…' : 'Save'}</button></div>
    </form>
  </section>
}
